import { Box, Button, Group, MantineSize, Paper, TableTrProps, Text } from '@mantine/core';
import { BaseRecord, SortableColumn, SortIconType } from './types';
import {
  IconArrowLeft,
  IconArrowRight,
  IconSortAscending,
  IconSortAscendingLetters,
  IconSortDescending,
  IconSortDescendingLetters,
} from '@tabler/icons-react';
import { DataTable, DataTableColumn, DataTableSortStatus } from 'mantine-datatable';
import { ReactNode } from 'react';
import { SearchHeader } from './SearchHeader';
import TableTitleBar from './TableTitleBar';
import { InfiniteScrollSentinel } from './InfiniteScrollSentinel';
import { PaginationData } from './hooks/useTable';

export type TableProps<T extends BaseRecord = BaseRecord> = {
  title?: string;
  subtitle?: string;
  titleIcon?: ReactNode;
  onTitleClick?: () => void;
  columns: DataTableColumn<T>[];
  data: T[];
  isLoading: boolean;
  onRowClick: (record: T) => void;
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  searchPlaceholder?: string;
  HeaderRow?: ReactNode;
  variant?: 'default' | 'clients-table';
  showCount?: boolean;
  titleSuffix?: ReactNode;
  striped?: boolean;
  withRowBorders?: boolean;
  headerStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  primaryButton?: {
    label: string;
    variant?: 'default' | 'primary';
    onClick: () => void;
  };
  pagination?: PaginationData;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  totalRecords?: number;
  sortStatus?: DataTableSortStatus<T>;
  onSortStatusChange?: (sortStatus: DataTableSortStatus<T>) => void;
  selectedRecords?: T[];
  onSelectedRecordsChange?: (records: T[]) => void;
  p?: MantineSize | number;
  verticalSpacing?: MantineSize | number;
  horizontalSpacing?: MantineSize | number;
  flat?: boolean;
  rowFactory?: (props: {
    record: T;
    index: number;
    children: React.ReactNode;
    rowProps: TableTrProps;
    expandedElement?: React.ReactNode;
  }) => React.ReactNode;
  tableWrapper?: ({ children }: { children: React.ReactNode }) => React.ReactNode;
};

export function Table<T extends BaseRecord = BaseRecord>({
  title,
  subtitle,
  titleIcon,
  onTitleClick,
  columns,
  data,
  isLoading,
  onRowClick,
  searchTerm,
  setSearchTerm,
  HeaderRow,
  primaryButton,
  variant = 'default',
  showCount = false,
  titleSuffix,
  striped = true,
  withRowBorders = true,
  headerStyle,
  cellStyle,
  pagination,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  totalRecords,
  sortStatus,
  onSortStatusChange,
  selectedRecords,
  onSelectedRecordsChange,
  p = 0,
  verticalSpacing,
  horizontalSpacing,
  flat = false,
  rowFactory,
  tableWrapper,
  searchPlaceholder,
}: TableProps<T>) {
  const dataTableStyles = {
    header: {
      backgroundColor: 'var(--mantine-color-gray-0)',
      fontSize: 'var(--mantine-font-size-xs)',
      fontWeight: 600,
      color: 'var(--mantine-color-gray-6)',
      ...headerStyle,
    },
    ...(cellStyle && { td: cellStyle }),
  };

  const hasCustomSortIcons = columns.some(
    column => (column as SortableColumn<T>).sortIconType !== undefined,
  );

  const displayColumns =
    hasCustomSortIcons && sortStatus
      ? columns.map(column => {
          const sortableColumn = column as SortableColumn<T>;
          if (!sortableColumn.sortable) return column;
          const isActive = sortStatus.columnAccessor === column.accessor;
          return {
            ...column,
            title: (
              <Group gap={4} wrap="nowrap" align="center">
                <SortIcon
                  type={sortableColumn.sortIconType ?? 'alpha'}
                  active={isActive}
                  direction={isActive ? sortStatus.direction : undefined}
                />
                {column.title}
              </Group>
            ),
          };
        })
      : columns;

  const sortIcons = hasCustomSortIcons ? { sorted: <></>, unsorted: <></> } : undefined;

  return (
    <>
      <Paper
        variant={flat ? undefined : 'content'}
        p={p}
        style={flat ? { border: 'none', boxShadow: 'none', background: 'transparent' } : undefined}
      >
        {title && (
          <TableTitleBar
            title={title}
            subtitle={subtitle}
            titleIcon={titleIcon}
            onTitleClick={onTitleClick}
            totalRecords={totalRecords}
            showCount={showCount}
            titleSuffix={titleSuffix}
            primaryButton={primaryButton}
          />
        )}
        <>
          {HeaderRow && (
            <Group px="lg" py="sm" justify="space-between">
              {HeaderRow}
            </Group>
          )}
          {searchTerm !== undefined && setSearchTerm && (
            <SearchHeader
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder={searchPlaceholder}
            />
          )}
        </>
        {sortStatus && onSortStatusChange ? (
          <DataTable<T>
            withTableBorder={false}
            withColumnBorders={false}
            withRowBorders={withRowBorders}
            borderColor="gray.3"
            rowBorderColor="gray.3"
            striped={striped}
            highlightOnHover
            columns={displayColumns}
            records={data}
            fetching={isLoading}
            minHeight={200}
            variant={variant}
            verticalSpacing={verticalSpacing}
            horizontalSpacing={horizontalSpacing}
            sortStatus={sortStatus}
            sortIcons={sortIcons}
            onSortStatusChange={onSortStatusChange}
            selectedRecords={selectedRecords}
            onSelectedRecordsChange={onSelectedRecordsChange}
            onCellClick={({ record, column }) => {
              if (column.accessor === 'actions') {
                return;
              }
              onRowClick(record);
            }}
            styles={dataTableStyles}
            rowFactory={rowFactory}
            tableWrapper={tableWrapper}
          />
        ) : (
          <DataTable<T>
            withTableBorder={false}
            withColumnBorders={false}
            withRowBorders={withRowBorders}
            borderColor="gray.3"
            rowBorderColor="gray.3"
            striped={striped}
            highlightOnHover
            columns={displayColumns}
            records={data}
            fetching={isLoading}
            minHeight={200}
            variant={variant}
            verticalSpacing={verticalSpacing}
            horizontalSpacing={horizontalSpacing}
            selectedRecords={selectedRecords}
            onSelectedRecordsChange={onSelectedRecordsChange}
            onCellClick={({ record, column }) => {
              if (column.accessor === 'actions') {
                return;
              }
              onRowClick(record);
            }}
            styles={dataTableStyles}
            rowFactory={rowFactory}
            tableWrapper={tableWrapper}
          />
        )}

        {pagination && pagination.range.length > 1 && (
          <Box px="lg" py="sm">
            <Group justify="space-between">
              <Button
                variant="outline-black"
                leftSection={<IconArrowLeft size={16} />}
                disabled={pagination.active === 1}
                onClick={() => pagination.previous()}
              >
                Previous
              </Button>
              <Group gap="xs">
                {pagination.range.map((pageNum: number | 'dots', index: number) => {
                  if (pageNum === 'dots') {
                    return (
                      <Text size="sm" fw={500} c="gray.10" key={index}>
                        ...
                      </Text>
                    );
                  }
                  return (
                    <Button
                      key={index}
                      variant="subtle"
                      data-active={pagination.active === pageNum}
                      w={40}
                      h={40}
                      p={0}
                      radius={8}
                      onClick={() => pagination.setPage(pageNum as number)}
                    >
                      <Text size="sm" fw={500} c="gray.10">
                        {pageNum}
                      </Text>
                    </Button>
                  );
                })}
              </Group>
              <Button
                variant="outline-black"
                rightSection={<IconArrowRight size={16} />}
                disabled={pagination.active === pagination.range[pagination.range.length - 1]}
                onClick={() => pagination.next()}
              >
                Next
              </Button>
            </Group>
          </Box>
        )}

        {fetchNextPage && (
          <InfiniteScrollSentinel
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={!!isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        )}
      </Paper>
    </>
  );
}

function SortIcon({
  type,
  active,
  direction,
}: {
  type: SortIconType;
  active: boolean;
  direction?: 'asc' | 'desc';
}) {
  const isDescending = active && direction === 'desc';
  const iconsByType = {
    date: { asc: IconSortAscending, desc: IconSortDescending },
    alpha: { asc: IconSortAscendingLetters, desc: IconSortDescendingLetters },
  } as const;
  const Icon = iconsByType[type][isDescending ? 'desc' : 'asc'];

  return (
    <Icon
      size={16}
      stroke={2}
      style={{ flexShrink: 0, aspectRatio: '1 / 1', opacity: active ? 1 : 0.5 }}
    />
  );
}
